import { getAsyncRequest, useUserState } from "@gx1/gx-npm-lib";
import { Fragment, useContext, useEffect } from "react";
import * as reactDeviceDetect from "react-device-detect";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { navigateToUrl } from "single-spa";
import { OnboardingUser } from "../../../app.constants";
import { AppContext } from "../../../app.context";
import MobileWarningOverlayComponent from "../../../ui/mobile-warning-overlay/mobile-warning-overlay.component";
import OnboardingOverlayComponent from "../../../ui/onboarding-overlay/onboarding-overlay.component";
import FirstTimeLogin from "./first-time-login/first-time-login.component";
import FormHeader from "./form-header/form-header.component";
import LoginButton from "./login-button/login-button.component";
import LoginFormFields from "./login-form-fields/login-form-fields.component";
import LoginFormOptions from "./login-form-options/login-form-options.component";
import { LoginFormContext } from "./login-form.context";
import styles from "./login-form.module.scss";
import { useRedirect } from "../../../hooks/use-redirect.hook";
import { useTrackParams } from "../../../hooks/use-track-params.hook";

const LoginForm = () => {
  const { t } = useTranslation();
  const {
    showMobileMessage,
    showOnboardingExperience,
    setOnboardingUser,
    setShowMobileMessage,
    setShowOnboardingExperience
  } = useContext(AppContext);
  const {
    email,
    isSuccessfulSso,
    isSuccessfulAuth,
    password,
    remember,
    showSso,
    setErrorText,
    setIsLoading,
    setIsSuccessfulSso,
    setIsSuccessfulAuth,
    setShowSso
  } = useContext(LoginFormContext);
  const { hasError, hasInitiatives, hasTasks, hasUserState, isEntitled, isOnboarded } = useUserState();
  const [searchParams] = useSearchParams();
  const loggedOut = searchParams.get("logout");
  const { hasTrackParams, trackParams } = useTrackParams();
  const { redirect } = useRedirect();

  useEffect(() => {
    if (hasUserState || loggedOut) {
      return;
    }
    (async () => {
      const response = await getAsyncRequest("api/v5/auth/init");
      if (response && response.status === 200) {
        setIsSuccessfulSso(true);
        setIsSuccessfulAuth(true);
        localStorage.setItem("genie_uid", response.data.data.gcomUserAuthToken);
        window.eventBus.publish("WS_CONNECT");
        window.eventBus.publish("SESSION_EVENT_LOAD");
        window.eventBus.publish("USER_STATE_LOAD");
      }
    })();
  }, [hasUserState, loggedOut, setIsSuccessfulSso, setIsSuccessfulAuth]);

  useEffect(() => {
    if (!hasUserState) {
      return;
    }
    const isSurveyRecipient = hasTasks && !hasInitiatives && !isEntitled;
    setOnboardingUser(isSurveyRecipient ? OnboardingUser.SURVEY_RECIPIENT : OnboardingUser.EVALUATOR);
  }, [hasInitiatives, hasTasks, hasUserState, isEntitled, setOnboardingUser]);

  useEffect(() => {
    if (!isSuccessfulAuth) {
      return;
    }
    if (!hasUserState) {
      return;
    }
    if (reactDeviceDetect.isMobile) {
      setShowMobileMessage(true);
      return;
    }
    if (!isOnboarded) {
      if (isSuccessfulSso) {
        setShowSso(true);
        return;
      }
      setShowOnboardingExperience(true);
    } else {
      navigateToUrl(redirect);
    }
  }, [
    isOnboarded,
    hasInitiatives,
    hasTasks,
    hasUserState,
    isSuccessfulSso,
    isEntitled,
    setOnboardingUser,
    setShowMobileMessage,
    setShowOnboardingExperience,
    setShowSso,
    t,
    isSuccessfulAuth,
    redirect
  ]);

  useEffect(() => {
    if (hasError) {
      setErrorText(t("Oops... something went wrong. Please try again."));
    }
  }, [hasError, setErrorText, t]);

  const handleSubmitForm = async () => {
    if (!(email && password)) {
      setErrorText(t("Please fill out all fields."));
      return;
    }
    setErrorText("");
    setIsLoading(true);
    try {
      const response = await fetch(
        `${window.gxApiConnection}/api/v5/auth/login${hasTrackParams ? `?${new URLSearchParams(trackParams).toString()}` : ""}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({ username: email, password, remember }),
          credentials: "include"
        }
      );
      const resJson: { data: { gcomUserAuthToken: string }, message?: string | string[] } = await response.json();
      if (response.ok) {
        setIsSuccessfulAuth(true);
        localStorage.setItem("genie_uid", resJson.data.gcomUserAuthToken);
        window.eventBus.publish("WS_CONNECT");
        window.eventBus.publish("USER_STATE_LOAD");
        window.eventBus.publish("SESSION_EVENT_LOAD");
      } else if (response.status === 401) {
        setErrorText(t("The email or password is not correct."));
      } else if (response.status === 403 && resJson.message === "NOT_ENTITLED") {
        setErrorText(t("This account does not have permission to access Gartner BuySmart. Please contact your Gartner Account Manager for assistance."));
      } else if (response.status === 403 && resJson.message === "FREE_TRIAL_EXPIRED") {
        setErrorText(t("Your free trial period has expired."));
      } else if (response.status === 400 && resJson.message?.includes("username must be an email")) {
        setErrorText(t("Please enter a valid email address."));
      } else {
        throw new Error();
      }
    } catch (error) {
      setErrorText(t("Oops... something went wrong. Please try again."));
    } finally {
      setIsLoading(false);
    }
  };

  const handleMobileMessageClose = () => {
    if (!isOnboarded) {
      setShowOnboardingExperience(true);
    } else {
      navigateToUrl(redirect);
    }
  };

  return (
    <Fragment>
      <div className={styles.loginForm}>
        {showSso && <FirstTimeLogin />}
        {!showSso && (
          <Fragment>
            <FormHeader />
            <form aria-label={t("login form")} noValidate autoComplete="off">
              <LoginFormFields onSubmit={handleSubmitForm} />
              <LoginFormOptions />
              <div className={styles.loginBtnWrapper}>
                <LoginButton disabled={!(email && password)} onSubmit={handleSubmitForm} />
              </div>
            </form>
          </Fragment>
        )}
      </div>
      <MobileWarningOverlayComponent onClose={handleMobileMessageClose} isOpen={showMobileMessage} />
      <OnboardingOverlayComponent isOpen={showOnboardingExperience} />
    </Fragment>
  );
};
export default LoginForm;
